import { ITCFConsentState } from "../TCFConsentProvider/types";
import { ITCFGlobalVendorList } from "types/tcf";
import { GVL, TCModel, TCString } from "@iabtechlabtcf/core";
import convertSemanticVersion from "./TCFConvertSemanticVersion";
import { APP_VERSION } from "core/config";
import TCFConvertConsentToSupportedFormat from "./TCFConvertConsentToSupportedFormat";
import {
  TCF_CMP_ID,
  TCF_VENDOR_CATEGORIES_TYPES,
  TCF_CONSENT_SCREEN,
} from "../constantsTCF";
import { FALSE, TRUE } from "constants/primitives";
import {
  MISSING_IAB_LIB_LEGINT_NAME,
  MISSING_IAB_LIB_NAME,
} from "constants/errors";

/**
 * Generate TCstring based on CMP info and user consents
 * @returns {string} The TC string representing the encoded consent information for accepted vendors.
 */
const TCFGenerateTCString = (
  gvl: ITCFGlobalVendorList | undefined | null,
  state: ITCFConsentState,
  userLang: string
): string => {
  // Check if correct gvl is present
  if (gvl) {
    const tcModel = new TCModel(new GVL(gvl));

    // Disable OOB signaling, we dont use them anyway and some clients reported issues with that - "Google AdSense Error 5.1"
    tcModel.supportOOB = FALSE;

    tcModel.cmpId = TCF_CMP_ID; // my CMP ID
    tcModel.cmpVersion = convertSemanticVersion(APP_VERSION); // my CMP Version
    tcModel.consentScreen = TCF_CONSENT_SCREEN; // On which 'screen' consent was captured;
    tcModel.consentLanguage = userLang;
    tcModel.publisherCountryCode = state.tcfCountry;
    tcModel.isServiceSpecific = TRUE;

    // Convert user consent to arrays where in array are only options that are enabled by user
    // Convert it to array ids as numbers. Expected result: [id1, id2, id3]
    tcModel.vendorConsents.set(
      TCFConvertConsentToSupportedFormat(state.userConsent.vendors)
    );
    tcModel.vendorLegitimateInterests.set(
      TCFConvertConsentToSupportedFormat(
        state.userConsent.vendorsLegitimateInterest
      )
    );

    // Add all vendors to list of disclosed vendors to user (requirement of TCF v2.3)
    state.vendorsList.forEach((vendor) => {
      tcModel.vendorsDisclosed.set(Number(vendor));
    });

    // Loop through all categories and add them
    TCF_VENDOR_CATEGORIES_TYPES.forEach((category) => {
      if (category.hasSwitch) {
        if (!category.IABLibName) {
          console.error(MISSING_IAB_LIB_NAME);
          return;
        }

        // Add purposes and special features if they exist in user consent
        if (state.userConsent.categories[category.key]) {
          tcModel[category.IABLibName].set(
            TCFConvertConsentToSupportedFormat(
              state.userConsent.categories[category.key]
            )
          );
        }

        if (category.hasLegitimateInterest) {
          if (!category.IABLibLegitimateInterestName) {
            console.error(MISSING_IAB_LIB_LEGINT_NAME);
            return;
          }

          if (
            state.userConsent.categoriesLegitimateInterest[category.key] &&
            category.hasLegitimateInterest
          ) {
            tcModel[category.IABLibLegitimateInterestName].set(
              TCFConvertConsentToSupportedFormat(
                state.userConsent.categoriesLegitimateInterest[category.key]
              )
            );
          }
        }
      }
    });

    // Encode to supported string
    return TCString.encode(tcModel);
  }

  return "";
};

export default TCFGenerateTCString;
